import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageResize from 'tiptap-extension-resize-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Paragraph from '@tiptap/extension-paragraph';
import { Mark, mergeAttributes, CommandProps } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import { useState, useEffect, useCallback } from 'react';
//import React, { useState, useEffect, useCallback, MouseEvent as ReactMouseEvent } from 'react';
//import styles from './AlbumViewer.module.css';
import './AlbumViewer.css';
import contentStyles from './EditorComponentContent.module.css';
import ReactComponent from './EditorExtension.jsx';
import ReactComponentCount from './EditorExtension_Count.jsx';
import ReactComponentRawHtml from './EditorExtension_RawHtml.jsx';
import ReactComponentLink from './EditorExtension_Link.jsx';
import ReactComponentAlbumLink from './EditorExtension_AlbumLink.jsx';
import ReactComponentAlbumEmbed from './EditorExtension_AlbumEmbed.jsx';
import ReactComponentMyImage from './EditorExtension_MyImage.jsx';
import ReactComponent_ImageList from './EditorExtension_ImageList.jsx';
import ReactComponent_RecordBrowser from './EditorExtension_RecordBrowser.jsx';
import ReactComponent_RecordBrowserPopup from './EditorExtension_RecordBrowserPopup.jsx';
import ReactComponent_AlbumViewer from './EditorExtension_AlbumViewer.jsx';
import AnnotatedImageViewer from './AnnotatedImageViewer';
import { DivNode } from './DivNode';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customSpan: {
      /**
       * Set a custom span with a class
       */
      setCustomSpan: (className: string) => ReturnType;
    }
  }
}

// AlbumViewer.tsx (CustomSpan extension)
const CustomSpan = Mark.create({
  name: 'customSpan',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('class'),
        renderHTML: (attributes: Record<string, any>) => {
          return { class: attributes.class };
        },
      },
      style: { // Handle style attribute as a CSS string
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('style'),
        renderHTML: (attributes: Record<string, any>) => {
          return { style: attributes.style };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span',
      },
    ];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, any> }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setCustomSpan: (className: string, style?: string) => ({ chain }: CommandProps) => {
        return chain()
          .setMark(this.name, { class: className, style }) // Pass style as CSS string
          .run();
      },
    };
  },
});



const CustomParagraph = Paragraph.extend({
  addAttributes() {
      return {
          class: {
              default: null,
              // Take the attribute values
              renderHTML: attributes => {
                  // … and return an object with HTML attributes.
                  return {
                      class: `${attributes.class}`,
                  }
              },
          },
      }
  },
})

type AlbumViewerComponentProps = {
  initialAlbumId?: string;
  albumSource?: string;
  isEditorMode?: boolean;
  ancestors?: string[];
};

const AlbumViewer: React.FC<AlbumViewerComponentProps> = ({ initialAlbumId, albumSource, isEditorMode = false, ancestors = [] }) => {
  const [content, setContent] = useState('');
  const [albumType, setAlbumType] = useState('');
  const [albumId, setAlbumId] = useState(initialAlbumId);

  const wouldCreateCycle = initialAlbumId ? ancestors.includes(initialAlbumId) : false;
  //console.log(initialAlbumId, " ancestors: ", ancestors);

  const newAncestors = initialAlbumId 
  ? [...ancestors, initialAlbumId]
  : ancestors;

  //console.log("new ancestors: ", newAncestors);

  //console.log("album source: ", albumSource);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ImageResize,
      CustomSpan,
      CustomParagraph,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'reactComponent', 'div'],
      }),
      TextStyle,
      Color,
      DivNode,
      TextStyle,
      Color,
      ReactComponent,
      ReactComponentCount,
      ReactComponentRawHtml,
      ReactComponentLink,
      ReactComponentAlbumEmbed.configure({
        ancestors: newAncestors
      }),
      ReactComponentAlbumLink.configure({
        ancestors: newAncestors
      }),
      ReactComponentMyImage,
      ReactComponent_ImageList,
      ReactComponent_RecordBrowser,
      ReactComponent_RecordBrowserPopup,
      ReactComponent_AlbumViewer.configure({
      isEditorMode: isEditorMode,
      }),
    ],
    editable: false,
    content: content,
  });

  const handleAlbumLinkClick = useCallback((event: Event) => {
    const customEvent = event as CustomEvent;
    const newAlbumId = customEvent.detail.albumId;
    if (newAlbumId && newAlbumId !== albumId) {
      setAlbumId(newAlbumId);
    }
  }, [albumId]);

  useEffect(() => {
    // Add event listener for albumLinkClick
    window.addEventListener('albumLinkClick', handleAlbumLinkClick);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('albumLinkClick', handleAlbumLinkClick);
    };
  }, [handleAlbumLinkClick]);

  useEffect(() => {
    const updateContent = async () => {
      //console.log("albumId: ", albumId,' albumSource: ', albumSource);
      if (albumSource) {
        setContent(albumSource);
        setAlbumType('album');
        if (editor) {
          try {
            //console.log("albumSource: ", albumSource);
            editor.commands.setContent(albumSource);
          } catch (error) {
            console.error("Failed to set content in editor:", error);
            // Optionally, set a fallback content
            editor.commands.setContent("<p>Error loading content.</p>");
          }
        }
      } else if (albumId) {
        try {
          const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}?action=getpublicalbum&albumId=${albumId}`);
          const data = await response.json();
          const newContent = data.albumText || '';
          setContent(newContent);
          if (data.nodeType==='annotatedImage') {
            setAlbumType('annotatedImage');
          } else {
            setAlbumType('album');
          }
          if (editor) {
            editor.commands.setContent(newContent);
          }
        } catch (error) {
          console.error("Failed to fetch album:", error);
        }
      }
    };

    updateContent();
  }, [albumId, albumSource, editor]);

  return (
    <span>
      {wouldCreateCycle ? (
        <span className="text-red-500">
          Circular reference detected: Album {initialAlbumId} is already in the hierarchy
        </span>
      ) : (
        <>
          {albumType === 'annotatedImage' && (
            <AnnotatedImageViewer imageName={""} annotationSource={content} />
          )}
          {albumType !== 'annotatedImage' && (
            <EditorContent editor={editor} className={contentStyles.editorContent} />
          )}
        </>
      )}
    </span>
  );
};

export default AlbumViewer;